import { format, parseISO } from 'date-fns';

interface LegalInfo {
    company_name?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    country?: string;
    siret?: string;
    tva?: string;
    capital?: string;
    ape?: string;
    siege_social?: string;
    rcs_ville?: string;
    rcs_numero?: string;
    phone?: string;
}

interface AbsencePdfData {
    id: string;
    employeeName: string;
    employeePosition?: string;
    employeeHireDate?: string;
    companyName: string;
    companyLogoUrl?: string;
    typeCode: string;
    dateStart: string;
    dateEnd: string;
    daysCount?: number;
    note?: string;
    status: string;
    createdAt?: string;
    approvedAt?: string;
    signatoryName?: string;
    signatureUrl?: string;
    employeeSignatureUrl?: string;
    legal?: LegalInfo | null;
    brandColor?: string;
}

const hexToRgb = (hex?: string): [number, number, number] => {
    const clean = (hex || '#987b55').replace('#', '');
    const r = parseInt(clean.substring(0, 2), 16) || 0;
    const g = parseInt(clean.substring(2, 4), 16) || 0;
    const b = parseInt(clean.substring(4, 6), 16) || 0;
    return [r, g, b];
};

const getContrastRgb = (hex?: string): [number, number, number] => {
    const [r, g, b] = hexToRgb(hex);
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.5 ? [0, 0, 0] : [255, 255, 255];
};

const clampChannel = (value: number) => Math.max(0, Math.min(255, Math.round(value)));
const rgbToHex = (r: number, g: number, b: number) =>
    `#${[r, g, b].map((channel) => clampChannel(channel).toString(16).padStart(2, '0')).join('')}`;

/** Extrait la couleur dominante/accentuée d'un logo via un canvas (même algorithme que le module Facture). */
const extractLogoAccentColor = async (logoUrl?: string): Promise<string | null> => {
    if (!logoUrl) return null;
    return await new Promise<string | null>((resolve) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => {
            try {
                const canvas = document.createElement('canvas');
                canvas.width = 64;
                canvas.height = 64;
                const ctx = canvas.getContext('2d');
                if (!ctx) { resolve(null); return; }

                ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
                const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);

                let weightedR = 0, weightedG = 0, weightedB = 0, totalWeight = 0;
                for (let i = 0; i < data.length; i += 4) {
                    const r = data[i], g = data[i + 1], b = data[i + 2], a = data[i + 3];
                    if (a < 100) continue;
                    const max = Math.max(r, g, b);
                    const min = Math.min(r, g, b);
                    const brightness = (r + g + b) / 3;
                    const saturation = max === 0 ? 0 : (max - min) / max;
                    if (brightness > 245) continue;
                    const weight = Math.max(0.15, saturation * 2.2 + ((255 - brightness) / 255));
                    weightedR += r * weight;
                    weightedG += g * weight;
                    weightedB += b * weight;
                    totalWeight += weight;
                }

                if (totalWeight <= 0) { resolve(null); return; }
                resolve(rgbToHex(weightedR / totalWeight, weightedG / totalWeight, weightedB / totalWeight));
            } catch {
                resolve(null);
            }
        };
        img.onerror = () => resolve(null);
        const src = String(logoUrl || '').trim();
        if (!src) { resolve(null); return; }
        img.src = src.startsWith('data:') ? src : `${src}${src.includes('?') ? '&' : '?'}accent=${Date.now()}`;
    });
};

const buildPdfDoc = async (data: AbsencePdfData) => {
    const { jsPDF } = await import('jspdf');
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

    const logoAccent = await extractLogoAccentColor(data.companyLogoUrl);
    const finalAccentHex = logoAccent || data.brandColor || '#2f9e9e';
    const primaryColor = hexToRgb(finalAccentHex);
    const primaryTextColor = getContrastRgb(finalAccentHex);
    const darkColor = [30, 41, 59];
    const lightBg = [248, 250, 252];

    const detectImageFormat = (url: string): 'PNG' | 'JPEG' | 'WEBP' => {
        if (!url) return 'PNG';
        const lower = url.toLowerCase();
        if (lower.includes('image/png') || lower.endsWith('.png')) return 'PNG';
        if (lower.includes('image/webp') || lower.endsWith('.webp')) return 'WEBP';
        if (lower.includes('image/jpg') || lower.includes('image/jpeg') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'JPEG';
        return 'PNG';
    };

    // ── Header (fond blanc, logo en haut à gauche à sa taille réelle, nom en dessous) ──
    if (data.companyLogoUrl) {
        const maxW = 40;
        const maxH = 24;
        let drawW = maxW;
        let drawH = maxH;
        try {
            const props = doc.getImageProperties(data.companyLogoUrl);
            const ratio = props.width / props.height;
            if (ratio > maxW / maxH) {
                drawW = maxW;
                drawH = maxW / ratio;
            } else {
                drawH = maxH;
                drawW = maxH * ratio;
            }
        } catch (e) {
            // Repli sur la boîte carrée par défaut si les dimensions réelles ne peuvent pas être lues
        }
        try {
            const fmt = detectImageFormat(data.companyLogoUrl);
            doc.addImage(data.companyLogoUrl, fmt, 15, 10, drawW, drawH);
        } catch (e) {
            try {
                doc.addImage(data.companyLogoUrl, 15, 10, drawW, drawH);
            } catch (e2) {
                console.warn('Could not render company logo in PDF:', e2);
            }
        }
    }

    // ── Title box ─────────────────────────────────────────────
    doc.setFillColor(primaryColor[0], primaryColor[1], primaryColor[2]);
    doc.roundedRect(15, 46, 180, 14, 3, 3, 'F');
    doc.setTextColor(primaryTextColor[0], primaryTextColor[1], primaryTextColor[2]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(13);
    doc.text("AUTORISATION D'ABSENCE", 105, 55, { align: 'center' });

    // ── Info grid ─────────────────────────────────────────────
    let y = 72;
    const leftX = 22;
    const valueX = 78;

    const fmtDate = (d?: string) => {
        if (!d) return '-';
        try {
            const clean = d.replace(' ', 'T');
            const parsed = parseISO(clean);
            if (!isNaN(parsed.getTime())) return format(parsed, 'dd/MM/yyyy');
            return d;
        } catch { return d; }
    };

    // Calculate dynamic info grid height
    let rowCount = 4; // employeeName, typeCode, dateStart/End, daysCount
    if (data.employeePosition) rowCount++;
    if (data.employeeHireDate) rowCount++;
    if (data.createdAt) rowCount++;
    if (data.approvedAt) rowCount++;
    if (data.note) rowCount++;

    const gridHeight = Math.max(75, rowCount * 10 + 10);
    doc.setFillColor(lightBg[0], lightBg[1], lightBg[2]);
    doc.roundedRect(15, 64, 180, gridHeight, 3, 3, 'F');
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(15, 64, 180, gridHeight, 3, 3, 'D');

    const addRow = (label: string, value: string, isBold: boolean = false) => {
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(9.5);
        doc.setTextColor(100, 116, 139);
        doc.text(label, leftX, y);
        doc.setFont('helvetica', isBold ? 'bold' : 'normal');
        doc.setTextColor(darkColor[0], darkColor[1], darkColor[2]);
        doc.text(value || '-', valueX, y);
        doc.setDrawColor(241, 245, 249);
        doc.line(leftX, y + 2.5, 188, y + 2.5);
        y += 9.5;
    };

    addRow('Salarié :', data.employeeName, true);
    if (data.employeePosition) addRow('Fonction / Poste :', data.employeePosition);
    if (data.employeeHireDate) addRow("Date d'ancienneté :", fmtDate(data.employeeHireDate));
    addRow("Type d'absence :", data.typeCode, true);
    addRow('Période accordée :', `Du ${fmtDate(data.dateStart)} au ${fmtDate(data.dateEnd)} (inclus)`);
    const displayDays = data.daysCount != null ? data.daysCount : '—';
    addRow('Durée comptabilisée :', `${displayDays} jour(s)`, true);
    if (data.createdAt) addRow('Date de la demande :', fmtDate(data.createdAt));
    if (data.approvedAt) addRow("Date d'acceptation :", fmtDate(data.approvedAt), true);
    if (data.note) addRow('Motif / Message :', data.note);

    // ── Status stamp ──────────────────────────────────────────
    const stampY = 64 + gridHeight + 6;
    doc.setFillColor(236, 253, 245);
    doc.setDrawColor(16, 185, 129);
    doc.roundedRect(15, stampY, 180, 16, 3, 3, 'FD');
    doc.setTextColor(5, 150, 105);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('DÉCISION : ACCEPTÉE ET VALIDÉE LE ' + (data.approvedAt ? fmtDate(data.approvedAt) : format(new Date(), 'dd/MM/yyyy')), 105, stampY + 10.5, { align: 'center' });

    // ── Signature block (right side) — responsable de l'établissement ──
    const sigY = stampY + 22;
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(110, sigY, 85, 45, 3, 3, 'FD');
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('Signature du responsable', 115, sigY + 7);
    doc.setTextColor(darkColor[0], darkColor[1], darkColor[2]);
    doc.setFontSize(9.5);
    doc.text(data.signatoryName || 'La Direction', 115, sigY + 14);

    if (data.signatureUrl) {
        try {
            const fmt = detectImageFormat(data.signatureUrl);
            doc.addImage(data.signatureUrl, fmt, 115, sigY + 16, 38, 24);
        } catch (e1) {
            try {
                const altFmt = data.signatureUrl.includes('image/png') ? 'JPEG' : 'PNG';
                doc.addImage(data.signatureUrl, altFmt, 115, sigY + 16, 38, 24);
            } catch (e2) {
                try {
                    doc.addImage(data.signatureUrl, 115, sigY + 16, 38, 24);
                } catch (e3) {
                    console.warn('Could not render signature image in PDF:', e3);
                }
            }
        }
    }

    // ── Signature block (left side) — salarié ──────────────────
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(226, 232, 240);
    doc.roundedRect(15, sigY, 85, 45, 3, 3, 'FD');
    doc.setTextColor(100, 116, 139);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.text('Signature du salarié', 20, sigY + 7);
    doc.setTextColor(darkColor[0], darkColor[1], darkColor[2]);
    doc.setFontSize(9.5);
    doc.text(data.employeeName || '', 20, sigY + 14);

    if (data.employeeSignatureUrl) {
        try {
            const fmt = detectImageFormat(data.employeeSignatureUrl);
            doc.addImage(data.employeeSignatureUrl, fmt, 20, sigY + 16, 38, 24);
        } catch (e1) {
            try {
                const altFmt = data.employeeSignatureUrl.includes('image/png') ? 'JPEG' : 'PNG';
                doc.addImage(data.employeeSignatureUrl, altFmt, 20, sigY + 16, 38, 24);
            } catch (e2) {
                try {
                    doc.addImage(data.employeeSignatureUrl, 20, sigY + 16, 38, 24);
                } catch (e3) {
                    console.warn('Could not render employee signature image in PDF:', e3);
                }
            }
        }
    }

    // ── Legal footer ──────────────────────────────────────────
    const L = data.legal;
    const footerY = 263;

    doc.setDrawColor(226, 232, 240);
    doc.line(15, footerY - 3, 195, footerY - 3);

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(100, 116, 139);
    const companyFooterName = L?.company_name || data.companyName;
    doc.text(companyFooterName.toUpperCase(), 105, footerY + 2, { align: 'center', charSpace: 0.5 });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(148, 163, 184);

    const legal1: string[] = [];
    if (L?.rcs_ville && L?.rcs_numero) legal1.push(`RCS ${L.rcs_ville} ${L.rcs_numero}`);
    if (L?.siret) legal1.push(`SIRET ${L.siret}`);
    if (L?.tva) legal1.push(`TVA ${L.tva}`);
    if (L?.ape) legal1.push(`Code APE ${L.ape}`);
    if (legal1.length > 0) doc.text(legal1.join(' • '), 105, footerY + 7, { align: 'center' });

    const legal2: string[] = [];
    if (L?.capital) legal2.push(`Capital : ${L.capital}`);
    if (L?.siege_social) legal2.push(L.siege_social);
    else if (L?.address) {
        const addr = [L.address, L.postal_code, L.city, L.country].filter(Boolean).join(', ');
        if (addr) legal2.push(addr);
    }
    if (legal2.length > 0) doc.text(legal2.join(' • '), 105, footerY + 12, { align: 'center' });

    // Ref line
    doc.setFontSize(6.5);
    doc.setTextColor(180, 180, 180);
    doc.text(`Réf. ${data.id} — Document généré le ${format(new Date(), 'dd/MM/yyyy à HH:mm')} — iAmani Connexion`, 105, footerY + 18, { align: 'center' });

    return doc;
};

export const generateAbsencePdf = async (data: AbsencePdfData) => {
    try {
        const doc = await buildPdfDoc(data);
        const cleanName = data.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
        doc.save(`Autorisation_Absence_${cleanName}_${data.id}.pdf`);
    } catch (e) {
        console.error('PDF Generation Error:', e);
        alert('Erreur lors de la génération du document PDF.');
    }
};

/** Returns PDF as base64 string (without downloading) */
export const generateAbsencePdfBase64 = async (data: AbsencePdfData): Promise<{ base64: string; fileName: string } | null> => {
    try {
        const doc = await buildPdfDoc(data);
        const cleanName = data.employeeName.replace(/[^a-zA-Z0-9]/g, '_');
        const fileName = `Autorisation_Absence_${cleanName}_${data.id}.pdf`;
        const base64 = doc.output('datauristring').split(',')[1];
        return { base64, fileName };
    } catch (e) {
        console.error('PDF Base64 Error:', e);
        return null;
    }
};
